import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Upload, ScanLine, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ScreenshotAI() {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const choose = (f) => {
    setFile(f);
    setResult(null);
    if (!f) { setPreview(null); return; }
    if (f.type === "application/pdf" || f.name?.toLowerCase().endsWith(".pdf")) {
      setPreview({ pdf: true, name: f.name });
    } else {
      setPreview({ pdf: false, url: URL.createObjectURL(f) });
    }
  };

  const analyze = async () => {
    if (!file) { toast.error("Bitte zuerst eine Datei auswählen"); return; }
    setAnalyzing(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/ai/screenshot-analysis", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Analyse abgeschlossen");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const toOrder = () => {
    if (!result) return;
    nav("/orders/new", {
      state: {
        prefill: {
          auftragsnummer: result.auftragsnummer || "",
          kls_id: result.kls_id || "",
          ausfuehrungsdatum: result.datum || new Date().toISOString().slice(0, 10),
          telekom_konto: result.telekom_konto || "",
        },
      },
    });
  };

  const openExisting = () => {
    if (result?.existing_order?.id) nav(`/orders/${result.existing_order.id}`);
  };

  const mergeToExisting = async () => {
    if (!result?.existing_order?.id || !result?.positionen?.length) return;
    try {
      const positions = result.positionen.map((p) => ({
        pos_nummer: String(p.pos_nummer || ""),
        beschreibung: p.beschreibung || "",
        menge: Number(p.menge || 0),
        einheit: p.einheit || "Stk",
      }));
      await api.post(`/orders/${result.existing_order.id}/aufmass/merge`, positions);
      toast.success("Aufmaß im bestehenden Auftrag ergänzt");
      // If confidence low, mark as conflict
      if (result.suggested_status === "Konflikt") {
        await api.put(`/orders/${result.existing_order.id}`, { status: "Konflikt" });
        toast.warning("Status auf 'Konflikt' gesetzt (niedrige KI-Konfidenz)");
      }
      nav(`/orders/${result.existing_order.id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const toAufmass = () => {
    if (!result?.positionen?.length) return;
    toast.info("Wähle zuerst den Auftrag im Aufmaß-Bereich.");
    nav("/aufmass", { state: { positions: result.positionen.map((p) => ({
      pos_nummer: String(p.pos_nummer || ""),
      beschreibung: p.beschreibung || "",
      menge: Number(p.menge || 0),
      einheit: p.einheit || "Stk",
    })) } });
  };

  return (
    <div data-testid="screenshot-ai">
      <PageHeader
        title="KI Screenshot Analyse"
        subtitle="Lade einen KASYS Screenshot hoch — die KI erkennt automatisch Auftragsnummer, KLS-ID, Positionen, Mengen"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-md p-5" style={{ borderColor: "#E2E8F0" }}>
          <label
            data-testid="ai-dropzone"
            className="block border-2 border-dashed rounded-md p-8 text-center cursor-pointer hover:border-[#0A2540] transition-colors"
            style={{ borderColor: "#CBD5E1", background: "#F8FAFC" }}
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf,.pdf"
              className="hidden"
              onChange={(e) => choose(e.target.files?.[0] || null)}
              data-testid="ai-file-input"
            />
            {!preview ? (
              <div className="text-slate-500">
                <Upload className="h-8 w-8 mx-auto mb-3 text-slate-400" />
                <div className="font-semibold text-[#0A2540]">Datei auswählen</div>
                <div className="text-xs mt-1">PNG · JPEG · WEBP · PDF</div>
              </div>
            ) : preview.pdf ? (
              <div className="py-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-md mb-3" style={{ background: "#FEE2E2" }}>
                  <span className="text-xs font-bold text-red-700">PDF</span>
                </div>
                <div className="text-sm font-semibold truncate max-w-[280px] mx-auto">{preview.name}</div>
                <div className="text-xs text-slate-500 mt-1">PDF wird vor der Analyse automatisch eingelesen</div>
              </div>
            ) : (
              <img src={preview.url} alt="Vorschau" className="max-h-96 mx-auto rounded" />
            )}
          </label>
          <div className="mt-4 flex gap-2">
            <Button
              data-testid="ai-analyze-btn"
              className="h-12 px-6 bg-[#0A2540] hover:bg-[#113A63] text-white"
              onClick={analyze}
              disabled={analyzing || !file}
            >
              <ScanLine className="h-4 w-4 mr-2" />{analyzing ? "Analysiert…" : "Analysieren"}
            </Button>
            {file && <Button variant="outline" className="h-12" onClick={() => choose(null)}>Zurücksetzen</Button>}
          </div>
        </div>

        <div className="bg-white border rounded-md p-5" style={{ borderColor: "#E2E8F0" }}>
          <h3 className="font-bold mb-3">Erkannte Werte</h3>
          {!result && <div className="text-sm text-slate-400 py-12 text-center">Noch keine Analyse</div>}
          {result && (
            <div data-testid="ai-result">
              <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-4 ${result.konfidenz === "hoch" ? "text-emerald-700" : result.konfidenz === "mittel" ? "text-amber-700" : "text-red-700"}`}>
                {result.konfidenz === "hoch" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} Konfidenz: {result.konfidenz || "—"}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs uppercase text-slate-500 font-semibold">Auftragsnummer</div><div className="font-semibold">{result.auftragsnummer || "—"}</div></div>
                <div><div className="text-xs uppercase text-slate-500 font-semibold">KLS-ID</div><div className="font-semibold mono">{result.kls_id || "—"}</div></div>
                <div><div className="text-xs uppercase text-slate-500 font-semibold">Datum</div><div className="font-semibold">{result.datum || "—"}</div></div>
                <div><div className="text-xs uppercase text-slate-500 font-semibold">Telekom Konto</div><div className="font-semibold">{result.telekom_konto || "—"}</div></div>
              </div>

              <div className="mt-5">
                <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Positionen ({(result.positionen || []).length})</div>
                <div className="border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr><th className="text-left px-3 py-2">Pos.-Nr.</th><th className="text-left px-3 py-2">Beschreibung</th><th className="text-right px-3 py-2">Menge</th><th className="text-left px-3 py-2">Einheit</th></tr>
                    </thead>
                    <tbody>
                      {(result.positionen || []).map((p, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: "#E2E8F0" }}>
                          <td className="px-3 py-2 mono">{p.pos_nummer}</td>
                          <td className="px-3 py-2">{p.beschreibung}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{p.menge}</td>
                          <td className="px-3 py-2">{p.einheit}</td>
                        </tr>
                      ))}
                      {(result.positionen || []).length === 0 && <tr><td colSpan={4} className="text-center py-4 text-slate-400">Keine Positionen erkannt</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                {result.existing_order ? (
                  <>
                    <div className="w-full bg-blue-50 border border-blue-200 rounded-md p-3 text-sm mb-2" data-testid="ai-existing-notice">
                      <strong>Auftrag bereits vorhanden:</strong> {result.existing_order.auftragsnummer} — Status {result.existing_order.status}
                    </div>
                    <Button className="h-11 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={openExisting} data-testid="ai-open-existing">
                      Vorhandenen Auftrag öffnen
                    </Button>
                    <Button variant="outline" className="h-11" onClick={mergeToExisting} disabled={!result?.positionen?.length} data-testid="ai-merge">
                      Aufmaß ergänzen
                    </Button>
                  </>
                ) : (
                  <>
                    <Button className="h-11 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={toOrder} data-testid="ai-to-order">
                      Als neuen Auftrag übernehmen
                    </Button>
                    <Button variant="outline" className="h-11" onClick={toAufmass} data-testid="ai-to-aufmass" disabled={!result?.positionen?.length}>
                      Ins Aufmaß übernehmen
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ActionBar hideNext />
    </div>
  );
}
